import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  IconButton,
  MenuItem,
  Rating,
  TextField,
  Typography
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/VerifiedOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOffAlt';
import AddPhotoIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../apiClient';
import { API_ENDPOINTS } from '../../constants';
import { ProductReviewsResponse, Review } from '../../types';
import { useAppSelector } from '../../hooks';
import { extractApiErrorMessage } from '../../utils/errorHandler';
import { loginWithRedirect } from '../../utils/navigation';
import { resolveImageUrl } from '../../utils/image';
import useStoreSettings from '../../hooks/useStoreSettings';

interface ProductReviewsProps {
  productId: string;
  ratingAverage?: number;
  ratingCount?: number;
}

const ProductReviews: React.FC<ProductReviewsProps> = ({ productId, ratingAverage, ratingCount }) => {
  const navigate = useNavigate();
  const { token } = useAppSelector((state) => state.auth);
  const storeSettings = useStoreSettings();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [distribution, setDistribution] = useState<Record<number, number>>({});
  const [totalApproved, setTotalApproved] = useState(0);
  const [sort, setSort] = useState('newest');
  const [starsFilter, setStarsFilter] = useState<number | null>(null);
  const [votedIds, setVotedIds] = useState<Set<string>>(new Set());
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [rating, setRating] = useState<number | null>(5);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    const params: Record<string, string> = {};
    if (sort !== 'newest') params.sort = sort;
    if (starsFilter) params.stars = String(starsFilter);
    apiClient
      .get(`${API_ENDPOINTS.PRODUCTS}/${productId}/reviews`, { params })
      .then((res) => {
        const data = res.data as ProductReviewsResponse;
        setReviews(data.reviews || []);
        setDistribution(data.distribution || {});
        setTotalApproved(data.total || 0);
        setVotedIds(new Set(data.myVotes || []));
      })
      .catch(() => {});
  }, [productId, sort, starsFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Review-request emails link to /products/<slug>?review=1
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('review') === '1' && token) {
      setFormOpen(true);
    }
  }, [searchParams, token]);

  const submit = async () => {
    if (!rating || comment.trim().length < 3) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post(`${API_ENDPOINTS.PRODUCTS}/${productId}/reviews`, {
        rating,
        title: title.trim(),
        comment: comment.trim(),
        ...(photos.length > 0 ? { images: photos } : {})
      });
      setSubmitted(true);
      setFormOpen(false);
      setTitle('');
      setComment('');
      setPhotos([]);
      setRating(5);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not submit your review'));
    } finally {
      setSubmitting(false);
    }
  };

  const uploadPhotos = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const remaining = 3 - photos.length;
    const selected = Array.from(files).slice(0, remaining);
    if (selected.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      selected.forEach((file) => formData.append('images', file));
      const response = await apiClient.post('/uploads/reviews', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const urls = (response.data?.urls as string[]) || [];
      setPhotos((prev) => [...prev, ...urls].slice(0, 3));
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Photo upload failed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const toggleHelpful = async (review: Review) => {
    if (!token) {
      navigate(loginWithRedirect(window.location.pathname));
      return;
    }
    try {
      const response = await apiClient.post(`${API_ENDPOINTS.REVIEWS}/${review._id}/helpful`);
      const { helpfulCount, voted } = response.data as { helpfulCount: number; voted: boolean };
      setReviews((prev) =>
        prev.map((r) => (r._id === review._id ? { ...r, helpfulCount } : r))
      );
      setVotedIds((prev) => {
        const next = new Set(prev);
        if (voted) next.add(review._id);
        else next.delete(review._id);
        return next;
      });
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not record your vote'));
    }
  };

  const rewardPercent = storeSettings?.reviewRewardPercent || 0;

  const startReview = () => {
    if (!token) {
      navigate(loginWithRedirect(window.location.pathname));
      return;
    }
    setFormOpen(true);
    setSubmitted(false);
  };

  return (
    <Box sx={{ mt: 6 }}>
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Customer reviews
          </Typography>
          {ratingCount && ratingCount > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Rating value={ratingAverage || 0} precision={0.1} readOnly size="small" />
              <Typography variant="body2" color="text.secondary">
                {ratingAverage?.toFixed(1)} out of 5 · {ratingCount} review
                {ratingCount === 1 ? '' : 's'}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No reviews yet — be the first to share your thoughts.
            </Typography>
          )}
        </Box>
        {!formOpen && (
          <Box sx={{ textAlign: 'right' }}>
            <Button variant="outlined" onClick={startReview}>
              Write a review
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Only verified buyers can review
            </Typography>
            {rewardPercent > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 600 }}>
                Get {rewardPercent}% off your next order when your review is approved
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {submitted && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Thanks for your review! It will appear here once our team approves it.
        </Alert>
      )}

      {totalApproved > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            mb: 2.5,
            p: 2,
            border: '1px solid rgba(0,0,0,0.07)',
            borderRadius: 2
          }}
        >
          {/* Star histogram — click a row to filter */}
          <Box sx={{ minWidth: 240, flex: 1, maxWidth: 340 }}>
            {[5, 4, 3, 2, 1].map((star) => {
              const count = distribution[star] || 0;
              const pct = totalApproved ? (count / totalApproved) * 100 : 0;
              const active = starsFilter === star;
              return (
                <Box
                  key={star}
                  onClick={() => setStarsFilter(active ? null : star)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.4,
                    cursor: 'pointer',
                    opacity: starsFilter && !active ? 0.45 : 1,
                    '&:hover': { opacity: 1 }
                  }}
                >
                  <Typography variant="caption" sx={{ width: 24, fontWeight: active ? 700 : 400 }}>
                    {star}★
                  </Typography>
                  <Box sx={{ flex: 1, height: 8, borderRadius: 99, backgroundColor: 'rgba(0,0,0,0.07)' }}>
                    <Box
                      sx={{
                        width: `${pct}%`,
                        height: '100%',
                        borderRadius: 99,
                        background: 'linear-gradient(90deg, #D65A88, #7061DA)'
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 26, textAlign: 'right' }}>
                    {count}
                  </Typography>
                </Box>
              );
            })}
            {starsFilter && (
              <Button size="small" onClick={() => setStarsFilter(null)} sx={{ mt: 0.5 }}>
                Show all ratings
              </Button>
            )}
          </Box>

          <TextField
            select
            size="small"
            label="Sort reviews"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="newest">Newest first</MenuItem>
            <MenuItem value="helpful">Most helpful</MenuItem>
            <MenuItem value="highest">Highest rated</MenuItem>
            <MenuItem value="lowest">Lowest rated</MenuItem>
          </TextField>
        </Box>
      )}

      {formOpen && (
        <Box sx={{ p: 2.5, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Your rating
          </Typography>
          <Rating value={rating} onChange={(_, value) => setRating(value)} sx={{ mb: 2 }} />
          <TextField
            fullWidth
            size="small"
            label="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Your review"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {photos.map((photo) => (
              <Box key={photo} sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  src={resolveImageUrl(photo)}
                  alt="Your photo"
                  sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1.5, display: 'block' }}
                />
                <IconButton
                  size="small"
                  aria-label="Remove photo"
                  onClick={() => setPhotos((prev) => prev.filter((p) => p !== photo))}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: 'background.paper',
                    boxShadow: 1,
                    p: 0.25,
                    '&:hover': { backgroundColor: 'background.paper' }
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
            {photos.length < 3 && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddPhotoIcon />}
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                sx={{ height: 64 }}
              >
                {uploading ? 'Uploading…' : photos.length > 0 ? 'Add more' : 'Add photos'}
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              multiple
              hidden
              onChange={(e) => uploadPhotos(e.target.files)}
            />
            <Typography variant="caption" color="text.secondary">
              Up to 3 photos (optional)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={submit}
              disabled={submitting || !rating || comment.trim().length < 3}
            >
              {submitting ? 'Submitting…' : 'Submit review'}
            </Button>
            <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          </Box>
        </Box>
      )}

      {reviews.map((review) => (
        <Box key={review._id} sx={{ py: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Rating value={review.rating} readOnly size="small" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {review.customerName}
            </Typography>
            {review.verifiedPurchase && (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
                label="Verified purchase"
                sx={{ height: 22 }}
              />
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {new Date(review.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
          {review.title && (
            <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
              {review.title}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {review.comment}
          </Typography>
          {(review.images?.length || 0) > 0 && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              {review.images!.map((image) => (
                <Box
                  key={image}
                  component="img"
                  src={resolveImageUrl(image)}
                  alt={`Photo by ${review.customerName}`}
                  onClick={() => setLightbox(image)}
                  sx={{
                    width: 72,
                    height: 72,
                    objectFit: 'cover',
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    border: '1px solid rgba(0,0,0,0.08)',
                    '&:hover': { opacity: 0.85 }
                  }}
                />
              ))}
            </Box>
          )}
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              color={votedIds.has(review._id) ? 'primary' : 'inherit'}
              startIcon={
                votedIds.has(review._id) ? (
                  <ThumbUpIcon sx={{ fontSize: 15 }} />
                ) : (
                  <ThumbUpOutlinedIcon sx={{ fontSize: 15 }} />
                )
              }
              onClick={() => toggleHelpful(review)}
              sx={{ textTransform: 'none', color: votedIds.has(review._id) ? undefined : 'text.secondary' }}
            >
              Helpful{(review.helpfulCount || 0) > 0 ? ` (${review.helpfulCount})` : ''}
            </Button>
          </Box>
          {review.reply?.text && (
            <Box
              sx={{
                mt: 1.5,
                ml: 2,
                p: 1.5,
                borderLeft: '3px solid',
                borderColor: 'primary.main',
                backgroundColor: '#FCF6F9',
                borderRadius: '0 8px 8px 0'
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>
                Response from Vediluxe ·{' '}
                {new Date(review.reply.repliedAt).toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {review.reply.text}
              </Typography>
            </Box>
          )}
        </Box>
      ))}

      {/* Full-size customer photo */}
      <Dialog open={Boolean(lightbox)} onClose={() => setLightbox(null)} maxWidth="md">
        {lightbox && (
          <Box
            component="img"
            src={resolveImageUrl(lightbox)}
            alt="Customer photo"
            sx={{ display: 'block', maxWidth: '100%', maxHeight: '85vh' }}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default ProductReviews;
